/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PageStoredFilterPageResponse } from '../models/page-stored-filter-page-response';
import { StoredFilterCreateRequest } from '../models/stored-filter-create-request';
import { StoredFilterCreateResponse } from '../models/stored-filter-create-response';

/**
 * Stored filters endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class StoredFiltersService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation updateStoredFilterPinning
   */
  static readonly UpdateStoredFilterPinningPath = '/filters/{filterId}/pin';

  /**
   * Pin the stored filter.
   *
   * Update pinning of the specified stored filter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateStoredFilterPinning()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateStoredFilterPinning$Response(
    params: {
      filterId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      StoredFiltersService.UpdateStoredFilterPinningPath,
      'put'
    );
    if (params) {
      rb.path('filterId', params.filterId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Pin the stored filter.
   *
   * Update pinning of the specified stored filter
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateStoredFilterPinning$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateStoredFilterPinning(
    params: {
      filterId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateStoredFilterPinning$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getStoredFilters
   */
  static readonly GetStoredFiltersPath = '/filters';

  /**
   * List the stored filters.
   *
   * List the stored filters to the owner merchant
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getStoredFilters()` instead.
   *
   * This method doesn't expect any request body.
   */
  getStoredFilters$Response(
    params: {
      type: 'PRODUCT_SORTIMENT_FILTER' | 'PRICING_FILTER';
      pinned?: boolean;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageStoredFilterPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      StoredFiltersService.GetStoredFiltersPath,
      'get'
    );
    if (params) {
      rb.query('type', params.type, {});
      rb.query('pinned', params.pinned, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageStoredFilterPageResponse>;
        })
      );
  }

  /**
   * List the stored filters.
   *
   * List the stored filters to the owner merchant
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getStoredFilters$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getStoredFilters(
    params: {
      type: 'PRODUCT_SORTIMENT_FILTER' | 'PRICING_FILTER';
      pinned?: boolean;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageStoredFilterPageResponse> {
    return this.getStoredFilters$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageStoredFilterPageResponse>) =>
          r.body as PageStoredFilterPageResponse
      )
    );
  }

  /**
   * Path part for operation createStoredFilter
   */
  static readonly CreateStoredFilterPath = '/filters';

  /**
   * Create stored filters.
   *
   * Create stored filters to the specified search interface
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createStoredFilter()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createStoredFilter$Response(
    params: {
      body: StoredFilterCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<StoredFilterCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      StoredFiltersService.CreateStoredFilterPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<StoredFilterCreateResponse>;
        })
      );
  }

  /**
   * Create stored filters.
   *
   * Create stored filters to the specified search interface
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createStoredFilter$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createStoredFilter(
    params: {
      body: StoredFilterCreateRequest;
    },
    context?: HttpContext
  ): Observable<StoredFilterCreateResponse> {
    return this.createStoredFilter$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<StoredFilterCreateResponse>) =>
          r.body as StoredFilterCreateResponse
      )
    );
  }

  /**
   * Path part for operation deleteStoredFilter
   */
  static readonly DeleteStoredFilterPath = '/filters/{filterId}';

  /**
   * Delete stored filter.
   *
   * Delete specified stored filter.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteStoredFilter()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteStoredFilter$Response(
    params: {
      filterId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      StoredFiltersService.DeleteStoredFilterPath,
      'delete'
    );
    if (params) {
      rb.path('filterId', params.filterId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete stored filter.
   *
   * Delete specified stored filter.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteStoredFilter$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteStoredFilter(
    params: {
      filterId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteStoredFilter$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }
}
